// Premium Today screen ("Premium 1 · Today", v2 fidelity pass 2026-10-23) — a VISUAL redesign of Home. Behind
// config/features.PREMIUM_TODAY_ENABLED; the previous Home stays in App.js for rollback.
//
// Data: only what the existing APIs return — the active plan weeks (/weekly-plan/all-active), the user's completed
// Garmin activities + per-km splits + connection status (/garmin/*, database reads), and the coach card the app
// already loads (/insights/workout-trend, passed in as `trend`). Opening or refreshing Today never calls a paid AI
// endpoint. A metric the API did not supply is left out (never 0 / placeholder). Planned, completed and missing are
// labelled. Colours: the Premium v2 palette, scoped to .premium-v2 (src/index.css) — the rest of the app is unchanged.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CalendarBlank, ClipboardText, ChatCircle, Pulse, CaretLeft, ThumbsUp, ThumbsDown, Check, Watch } from '@phosphor-icons/react';
import authFetch from '../../utils/authFetch';
import { fetchGarminActivities, fetchGarminSplits, fetchGarminStatus } from '../../utils/garmin';
import { deepAnalysisButton } from '../home2/HomeA2';
import { garminHomeState } from '../home2/TrainingSourcesCard';
import { requestTrainingConnectionsScroll } from '../TrainingConnectionsSection';
import {
  isoOf, headerDate, weekDays, weekRangeLabel, planDaysByDate, hasRunningPlan, runsOn, trendActivity, dayStatus,
  formatDuration, startTime, metricTiles, aerobicTE, paceBars, plannedPace, mainSet, zoneChip, weekSummary,
  nextWorkoutView, dayMonth, paceChartLabel, weekChartLabel, paceBarLabel, weekBarLabel,
} from './todayModel';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';
const CARD = 'rounded-[18px] bg-[var(--p-card)] border border-[var(--p-border)]';
const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--p-accent)] focus-visible:outline-offset-2';
const MUTED = 'text-[var(--p-muted)]';
const TEXT = 'text-[var(--p-text)]';

function Chip({ tone = 'muted', children, testid }) {
  const cls = {
    done: 'bg-[var(--p-done-bg)] text-[var(--p-done)]',
    violet: 'bg-[var(--p-violet-bg)] text-[var(--p-violet)]',
    accent: 'bg-[var(--p-accent-soft)] text-[var(--p-accent)]',
    muted: `bg-[var(--p-inset)] ${MUTED}`,
  }[tone];
  return (
    <span data-testid={testid}
          className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-[3px] text-[12px] font-bold ${cls}`}>
      {children}
    </span>
  );
}

function Skel({ className = '' }) {
  return <div className={`animate-pulse rounded-[12px] bg-[var(--p-inset)] ${className}`} />;
}

function DayStrip({ days, selected, todayIso, statusOf, onSelect }) {
  return (
    <div className="grid grid-cols-7 gap-[4px]" role="tablist" aria-label="ימי השבוע">
      {days.map((d) => {
        const st = statusOf(d.iso);
        const sel = d.iso === selected;
        const dot = st === 'completed' ? 'bg-[var(--p-green)]' : (st === 'planned' ? 'bg-[var(--p-muted)] opacity-60' : null);
        const label = `${d.name} ${d.dayNum}${d.iso === todayIso ? ', היום' : ''}${st === 'completed' ? ', בוצע' : st === 'planned' ? ', מתוכנן' : ''}`;
        return (
          <button key={d.iso} type="button" role="tab" aria-selected={sel} aria-label={label} data-testid={`day-${d.iso}`}
                  onClick={() => onSelect(d.iso)}
                  className={`${FOCUS} flex flex-col items-center justify-center rounded-[12px] h-[58px] border transition-colors ${
                    sel ? 'bg-[var(--p-accent)] border-[var(--p-accent)] text-[var(--p-bg)]'
                        : `bg-[var(--p-card)] border-[var(--p-border)] ${TEXT}`}`}>
            <span className={`text-[12px] font-semibold ${sel ? '' : MUTED}`}>{d.letter}</span>
            <span className="text-[18px] font-extrabold tabular-nums leading-tight">{d.dayNum}</span>
            <span className={`mt-0.5 w-[5px] h-[5px] rounded-full ${dot ? (sel ? 'bg-[var(--p-bg)]' : dot) : 'bg-transparent'}`}
                  aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}

// Up to FIT_BARS kilometres every bar fits the card (flex). A longer run (half / 30 km / marathon) keeps a touch-sized
// bar per measured kilometre and scrolls horizontally INSIDE the bar strip only — the title, the range and the picked
// value stay put, the page never scrolls sideways, and a soft fade marks the side with more bars.
export const FIT_BARS = 15;
const SCROLL_BAR_W = 24;                              // px: WCAG 2.5.8 minimum target size

function PaceChart({ data }) {
  const [sel, setSel] = useState(null);              // index of the tapped bar (touch / click / keyboard)
  const [more, setMore] = useState({ start: false, end: false });
  const stripRef = useRef(null);
  const scroll = !!data && data.bars.length > FIT_BARS;
  const measure = () => {
    const el = stripRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const pos = Math.abs(el.scrollLeft);             // RTL: 0 at km 1 (right), negative towards the end in Chrome
    setMore({ start: pos > 2, end: max - pos > 2 });
  };
  useLayoutEffect(() => {
    if (!scroll) return undefined;
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [scroll, data]);                                // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return null;
  const picked = sel !== null ? data.bars[sel] : null;
  const bars = data.bars.map((b, i) => (
    <button key={b.key} type="button" aria-pressed={sel === i} aria-label={paceBarLabel(b)}
            data-testid={`pace-bar-${b.key}`} onClick={() => setSel(sel === i ? null : i)}
            className={`${FOCUS} ${scroll ? 'shrink-0' : 'flex-1'} h-full flex items-end p-0 bg-transparent border-none cursor-pointer rounded-[4px]`}
            style={scroll ? { width: SCROLL_BAR_W } : undefined}>
      <span aria-hidden="true"
            className={`block w-full rounded-[4px] ${b.fastest ? 'bg-[var(--p-green)]' : 'bg-[var(--p-bar)]'} ${
              sel === i ? 'ring-2 ring-[var(--p-text)]' : ''} ${sel !== null && sel !== i ? 'opacity-60' : ''}`}
            style={{ height: `${Math.round(b.rel * 100)}%` }} />
    </button>
  ));
  return (
    <div className="mt-4" data-testid="pace-chart" role="group" aria-label={paceChartLabel(data)}>
      <div className={`flex items-center justify-between gap-2 text-[12px] font-medium ${MUTED}`}>
        <span data-testid="pace-detail" aria-live="polite"
              className={picked ? 'font-bold text-[var(--p-text)] truncate' : 'truncate'}>
          {picked ? paceBarLabel(picked) : 'קצב לפי ק״מ'}
        </span>
        <span dir="ltr" className="tabular-nums shrink-0">{data.range}</span>
      </div>
      {scroll ? (
        <div className="relative mt-2">
          <div ref={stripRef} onScroll={measure} dir="rtl" data-testid="pace-strip"
               className="overflow-x-auto overflow-y-hidden scrollbar-hide" style={{ overscrollBehaviorX: 'contain' }}>
            <div className="flex items-end gap-[4px] h-[62px] w-max p-[3px]">{bars}</div>
          </div>
          {more.start && (
            <div aria-hidden="true" data-testid="pace-fade-start"
                 className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-[var(--p-card)] to-transparent" />
          )}
          {more.end && (
            <div aria-hidden="true" data-testid="pace-fade-end"
                 className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-[var(--p-card)] to-transparent" />
          )}
        </div>
      ) : (
        <div className="mt-2 flex items-end gap-[5px] h-[62px]" dir="rtl">{bars}</div>
      )}
    </div>
  );
}

// Garmin's Aerobic Training Effect (0-5), as the watch recorded it — drawn only when the API supplied it.
function TERing({ value }) {
  const R = 54;
  const C = 2 * Math.PI * R;
  const frac = Math.max(0, Math.min(1, value / 5));
  return (
    <div className="relative w-[124px] h-[124px] shrink-0" data-testid="te-ring" role="img"
         aria-label={`השפעה אירובית ${value.toFixed(1)} מתוך 5`}>
      <svg viewBox="0 0 128 128" className="w-full h-full -rotate-90" aria-hidden="true">
        <defs>
          <linearGradient id="teGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--p-ring-a)" />
            <stop offset="100%" stopColor="var(--p-ring-b)" />
          </linearGradient>
        </defs>
        <circle cx="64" cy="64" r={R} fill="none" stroke="var(--p-inset)" strokeWidth="12" />
        <circle cx="64" cy="64" r={R} fill="none" stroke="url(#teGrad)" strokeWidth="12" strokeLinecap="round"
                strokeDasharray={`${(C * frac).toFixed(1)} ${C.toFixed(1)}`} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" aria-hidden="true">
        <span className={`text-[36px] font-extrabold tabular-nums leading-none ${TEXT}`} dir="ltr">{value.toFixed(1)}</span>
        <span className={`mt-1 text-[12px] font-medium text-center leading-tight ${MUTED}`}>השפעה<br />אירובית</span>
      </div>
    </div>
  );
}

function Stat({ value, label, testid }) {
  return (
    <div data-testid={testid} className="text-center">
      <div className={`text-[22px] font-extrabold tabular-nums ${TEXT}`} dir="ltr">{value}</div>
      <div className={`text-[12px] ${MUTED}`}>{label}</div>
    </div>
  );
}

function OtherRuns({ runs, shownId, onPick }) {
  if (runs.length < 2) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="other-runs" role="group" aria-label="פעילויות ביום הזה">
      {runs.map((r) => {
        const on = r.garmin_id === shownId;
        const t = startTime(r);
        return (
          <button key={r.activityId || r.garmin_id} type="button" aria-pressed={on} onClick={() => onPick(r.garmin_id)}
                  data-testid={`run-${r.garmin_id}`}
                  className={`${FOCUS} rounded-full px-3 py-1 text-[12px] font-semibold border cursor-pointer ${
                    on ? 'bg-[var(--p-accent-soft)] text-[var(--p-accent)] border-[var(--p-accent)]'
                       : `bg-[var(--p-inset)] ${MUTED} border-[var(--p-border)]`}`}>
            <span dir="ltr" className="tabular-nums">{Number(r.distance).toFixed(2)}</span> ק״מ{t ? <> · <span dir="ltr">{t}</span></> : null}
          </button>
        );
      })}
    </div>
  );
}

function WorkoutCard({ iso, todayIso, act, runs, onPickRun, planDay, splits, noPlan, onOpenPlan }) {
  const status = dayStatus(iso, todayIso, !!act, planDay);
  const chip = zoneChip(planDay);
  if (status === 'none' && noPlan) {                         // e. no plan yet -> the existing plan screen / flow
    return (
      <div className={`${CARD} p-[18px]`} data-testid="workout-card" data-status="no-plan">
        <div className={`text-[18px] font-bold ${TEXT}`}>עדיין אין תכנית אימונים</div>
        <p className={`mt-1 mb-0 text-[14px] leading-relaxed ${MUTED}`}>התכנית שלך תופיע כאן ברגע שתיבנה.</p>
        <button type="button" onClick={onOpenPlan} data-testid="open-plan"
                className={`${FOCUS} mt-3 inline-flex items-center gap-1 text-[14px] font-bold text-[var(--p-accent)] bg-transparent border-none p-0 cursor-pointer`}>
          לתכנית האימונים <CaretLeft size={14} weight="bold" />
        </button>
      </div>
    );
  }
  if (status === 'rest' || status === 'none') {
    return (
      <div className={`${CARD} p-[18px]`} data-testid="workout-card" data-status={status}>
        {status === 'rest' && <Chip tone="muted">יום מנוחה</Chip>}
        <div className={`mt-3 text-[18px] font-bold ${TEXT}`}>
          {status === 'rest' ? 'מנוחה והתאוששות' : 'אין אימון מתוכנן ליום הזה'}
        </div>
      </div>
    );
  }
  if (status === 'completed') {
    const time = startTime(act);
    const dur = formatDuration(act.duration);
    const tiles = metricTiles(act);
    const te = aerobicTE(act);
    const provider = act.source === 'garmin' ? (act.device_model ? `Garmin ${act.device_model}` : 'Garmin') : null;
    const numbers = (
      <div className="min-w-0 flex flex-col items-start">
        <div className={`text-[44px] font-extrabold tabular-nums leading-none ${TEXT}`} dir="ltr" data-testid="distance">
          {Number(act.distance).toFixed(2)}
        </div>
        <div className={`mt-1 text-[13px] font-semibold ${MUTED}`}>ק״מ</div>
        <div className="mt-3 flex gap-6">
          {dur && <Stat value={dur} label="זמן" testid="duration" />}
          {act.averagePace && <Stat value={act.averagePace} label="דק׳/ק״מ" testid="pace" />}
        </div>
      </div>
    );
    return (
      <div className={`${CARD} p-[18px]`} data-testid="workout-card" data-status="completed">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 shrink-0">
            <Chip tone="done" testid="status-chip">{time ? `בוצע · ${time}` : 'בוצע'}</Chip>
            {chip && <Chip tone="violet">{chip}</Chip>}
          </div>
          {provider && (
            <span dir="ltr" className={`text-[12px] font-medium truncate min-w-0 max-w-[45%] ${MUTED}`} data-testid="provider">
              {provider}
            </span>
          )}
        </div>
        <div className={`mt-3 text-[20px] font-extrabold ${TEXT}`}>{(planDay && planDay.type) || 'ריצה'}</div>
        {te !== null ? (
          <div className="mt-3 flex items-center justify-between gap-3">
            <TERing value={te} />
            {numbers}
          </div>
        ) : (
          <div className="mt-3">{numbers}</div>
        )}
        {tiles.length > 0 && (
          <div className="mt-4 grid gap-[1px] rounded-[12px] overflow-hidden bg-[var(--p-inset-div)]"
               style={{ gridTemplateColumns: `repeat(${tiles.length}, minmax(0,1fr))` }} data-testid="metric-tiles">
            {tiles.map((t) => (
              <div key={t.key} className="bg-[var(--p-inset)] py-2.5 text-center" data-testid={`tile-${t.key}`}>
                <div className={`text-[18px] font-extrabold tabular-nums ${TEXT}`}>{t.value}</div>
                <div className={`text-[11px] font-medium ${MUTED}`}>{t.label}</div>
              </div>
            ))}
          </div>
        )}
        <PaceChart data={paceBars(splits)} />
        <OtherRuns runs={runs} shownId={act.garmin_id} onPick={onPickRun} />
        {act.fromCard && <div className={`mt-3 text-[12px] ${MUTED}`}>נתונים מתוך האימון שנרשם</div>}
      </div>
    );
  }
  // c. planned (today / future) or a past planned day with no recorded activity
  const pace = plannedPace(planDay);
  const set = mainSet(planDay);
  return (
    <div className={`${CARD} p-[18px]`} data-testid="workout-card" data-status={status}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Chip tone={status === 'missed' ? 'muted' : 'accent'} testid="status-chip">
          {status === 'missed' ? 'לא נרשמה פעילות' : (iso === todayIso ? 'מתוכנן להיום' : 'מתוכנן')}
        </Chip>
        {chip && <Chip tone="violet">{chip}</Chip>}
      </div>
      <div className={`mt-3 text-[20px] font-extrabold ${TEXT}`}>{planDay.type || 'אימון'}</div>
      <div className="mt-3 flex items-end gap-6">
        {planDay.km > 0 && (
          <div>
            <div className={`text-[40px] font-extrabold tabular-nums leading-none ${TEXT}`} dir="ltr">{planDay.km}</div>
            <div className={`mt-1 text-[13px] font-semibold ${MUTED}`}>ק״מ מתוכננים</div>
          </div>
        )}
        {pace && <Stat value={pace} label="קצב יעד" />}
      </div>
      {set && <div className={`mt-3 text-[14px] font-semibold ${TEXT}`}>{set}</div>}
      {planDay.description && (
        <p className={`mt-2 mb-0 text-[13px] leading-relaxed line-clamp-3 ${MUTED}`}>{planDay.description}</p>
      )}
    </div>
  );
}

function DeviceRow({ garmin, onConnect }) {
  const st = garminHomeState(garmin);
  if (garmin && garmin.syncing) {                            // g. syncing -> skeleton row
    return (
      <div className={`${CARD} p-4 flex items-center gap-3`} data-testid="sync-row" role="status" aria-live="polite">
        <Skel className="w-9 h-9 rounded-full" />
        <div className="flex-1">
          <div className={`text-[13px] font-semibold ${MUTED}`}>מסנכרן נתונים מ-Garmin…</div>
          <Skel className="mt-2 h-2.5 w-2/3" />
        </div>
      </div>
    );
  }
  if (st !== 'disconnected' && st !== 'attention') return null;
  return (                                                    // f. no connected device -> the existing connect flow
    <div className={`${CARD} p-4 flex items-center gap-3`} data-testid="device-row">
      <div className="w-9 h-9 rounded-full bg-[var(--p-inset)] text-[var(--p-accent)] flex items-center justify-center shrink-0">
        <Watch size={18} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-[14px] font-bold ${TEXT}`}>{st === 'attention' ? 'Garmin דורש חיבור מחדש' : 'השעון לא מחובר'}</div>
        <div className={`text-[12px] ${MUTED}`}>הביצוע והמדדים יופיעו כאן אחרי חיבור Garmin</div>
      </div>
      <button type="button" onClick={onConnect} data-testid="connect-device"
              className={`${FOCUS} shrink-0 rounded-full px-3 py-1.5 text-[13px] font-bold bg-[var(--p-accent)] text-[var(--p-bg)] border-none cursor-pointer`}>
        {st === 'attention' ? 'חיבור מחדש' : 'חיבור Garmin'}
      </button>
    </div>
  );
}

function InsightCard({ trend, pendingContext, contextSaving, onContextSave, onContextSkip, onDeepAnalyze,
  deepAnalyzing, onViewDeepAnalysis }) {
  const [sent, setSent] = useState(false);
  if (!trend || !trend.insight_text) return null;
  const tw = trend.today_workout || {};
  const prov = [dayMonth(tw.date), tw.label].filter(Boolean).join(' · ');
  const awaiting = !!pendingContext && !sent;
  const submit = (flag) => { if (contextSaving) return; setSent(true); onContextSave && onContextSave(flag); };
  const btn = onDeepAnalyze && tw.date ? deepAnalysisButton(trend, deepAnalyzing, onDeepAnalyze, onViewDeepAnalysis) : null;
  const small = `${FOCUS} inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] font-bold border cursor-pointer disabled:opacity-50`;
  return (
    <div className={`${CARD} p-4`} data-testid="insight-card">
      <div className="flex items-start gap-3">
        <img src="/logo192.png" alt="" className="w-9 h-9 rounded-[10px] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[12px] font-bold text-[var(--p-accent)]">תובנת המאמן</span>
            {prov && <span className={`text-[11px] truncate ${MUTED}`}>{prov}</span>}
          </div>
          <p className={`mt-0.5 mb-0 text-[15px] leading-snug font-medium ${TEXT}`}>{trend.insight_text}</p>
          {(awaiting || sent || btn) && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              {sent && (
                <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-[var(--p-done)]">
                  <Check size={13} weight="bold" />האימון נשמר
                </span>
              )}
              {awaiting && (
                <>
                  <button type="button" disabled={contextSaving} onClick={() => submit('great')}
                          className={`${small} bg-[var(--p-done-bg)] text-[var(--p-done)] border-transparent`}>
                    <ThumbsUp size={13} weight="bold" />הרגיש טוב
                  </button>
                  <button type="button" disabled={contextSaving} onClick={() => submit('hard_day')}
                          className={`${small} bg-[var(--p-orange-bg)] text-[var(--p-orange)] border-transparent`}>
                    <ThumbsDown size={13} weight="bold" />היה קשה
                  </button>
                  <button type="button" onClick={() => { setSent(false); onContextSkip && onContextSkip(); }}
                          className={`${FOCUS} bg-transparent border-none text-[12px] font-medium p-1 cursor-pointer ${MUTED}`}>דלג</button>
                </>
              )}
              {btn && (
                <button type="button" data-testid="deep-analysis-button" data-state={btn.state} disabled={btn.disabled}
                        onClick={() => { if (!btn.disabled && btn.onClick) btn.onClick(); }}
                        className={`${small} bg-transparent text-[var(--p-accent)] border-[var(--p-accent)]`}>
                  {btn.label}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NextWorkout({ view, onOpen }) {
  if (!view) return null;
  return (
    <button type="button" onClick={onOpen} data-testid="next-workout"
            className={`${CARD} ${FOCUS} w-full p-4 flex items-center gap-3 text-right cursor-pointer`}>
      {view.dayNum !== null && (
        <div className="w-[52px] h-[52px] rounded-[12px] bg-[var(--p-orange-bg)] text-[var(--p-orange)] flex flex-col items-center justify-center shrink-0">
          <span className="text-[12px] font-bold">{view.dayName}</span>
          <span className="text-[20px] font-extrabold tabular-nums leading-none">{view.dayNum}</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className={`text-[12px] font-medium ${MUTED}`}>האימון הבא</div>
        <div className={`text-[17px] font-extrabold truncate ${TEXT}`}>{view.title}</div>
        {(view.set || view.pace) && (
          <div className={`text-[13px] truncate ${MUTED}`} data-testid="next-sub">
            {view.set && <bdi>{view.set}</bdi>}
            {view.set && view.pace && ' · '}
            {view.pace && <span dir="ltr" className="tabular-nums">{view.pace}</span>}
          </div>
        )}
      </div>
      <CaretLeft size={18} weight="bold" className={`shrink-0 ${MUTED}`} />
    </button>
  );
}

function WeekCard({ summary, range }) {
  const [sel, setSel] = useState(null);              // index of the tapped day (touch / click / keyboard)
  if (!summary) return null;
  const planned = summary.basis === 'planned';
  return (
    <div className={`${CARD} p-4`} data-testid="week-card" data-basis={summary.basis}>
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-[13px] font-semibold ${MUTED}`}>
            השבוע · <span dir="ltr">{range}</span>{planned ? ' · מתוכנן' : ''}
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className={`text-[34px] font-extrabold tabular-nums leading-none ${TEXT}`} dir="ltr">{summary.km}</span>
            <span className={`text-[14px] font-semibold ${MUTED}`}>ק״מ</span>
          </div>
        </div>
        <div className={`text-[13px] text-left leading-relaxed ${MUTED}`}>
          {summary.count !== null && <div>{summary.count === 1 ? 'פעילות אחת' : `${summary.count} פעילויות`}</div>}
          <div>{summary.runDays === 1 ? 'יום ריצה אחד' : `${summary.runDays} ימי ריצה`}</div>
          {!planned && summary.plannedKm && <div>{`מתוך ${summary.plannedKm} ק״מ מתוכננים`}</div>}
        </div>
      </div>
      <div role="group" aria-label={weekChartLabel(summary)}>
        <div className={`mt-3 h-[18px] text-[13px] font-semibold truncate ${TEXT}`} data-testid="week-detail" aria-live="polite">
          {sel !== null ? weekBarLabel(summary.bars[sel], summary.basis) : ''}
        </div>
        <div className="mt-1 grid grid-cols-7 gap-2 items-end h-[70px]">
          {summary.bars.map((b, i) => (
            <button key={b.iso} type="button" aria-pressed={sel === i} aria-label={weekBarLabel(b, summary.basis)}
                    data-testid={`week-bar-${b.iso}`} onClick={() => setSel(sel === i ? null : i)}
                    className={`${FOCUS} flex flex-col items-center justify-end h-full p-0 bg-transparent border-none cursor-pointer rounded-[6px]`}>
              <span aria-hidden="true"
                    className={`block w-full rounded-[6px] ${b.km > 0 ? (b.isToday ? 'bg-[var(--p-accent)]' : 'bg-[var(--p-bar)]') : 'bg-[var(--p-border)]'} ${planned && b.km > 0 ? 'opacity-50' : ''} ${
                      sel === i ? 'ring-2 ring-[var(--p-text)]' : ''}`}
                    style={{ height: b.km > 0 ? `${Math.max(10, Math.round(b.rel * 100))}%` : '2px' }} />
            </button>
          ))}
        </div>
        <div className={`mt-1.5 grid grid-cols-7 gap-2 text-center text-[11px] ${MUTED}`} aria-hidden="true">
          {summary.bars.map((b) => <span key={b.iso}>{b.letter}</span>)}
        </div>
      </div>
    </div>
  );
}

/** The fixed bottom bar of the four main screens (Today · Plan · Coach · Activities) — ONE shared component. */
export function PremiumBottomNav({ active = 'home', onNavigate }) {
  const items = [
    { key: 'home', label: 'היום', Icon: CalendarBlank },
    { key: 'weekly-plan', label: 'תכנית', Icon: ClipboardText },
    { key: 'chat', label: 'מאמן', Icon: ChatCircle },
    { key: 'activities', label: 'אימונים', Icon: Pulse },
  ];
  return (
    <nav className="premium-v2 premium-bottom-nav fixed bottom-0 inset-x-0 z-30 bg-[var(--p-nav-bg)] border-t border-[var(--p-border)]"
         dir="rtl" data-testid="bottom-nav" aria-label="ניווט ראשי">
      <div className="max-w-lg mx-auto grid grid-cols-4 px-2 pt-2 pb-2">
        {items.map(({ key, label, Icon }) => {
          const on = key === active;
          return (
            <button key={key} type="button" onClick={() => onNavigate && onNavigate(key)} aria-current={on ? 'page' : undefined}
                    data-testid={`nav-${key}`}
                    className={`${FOCUS} rounded-[12px] flex flex-col items-center gap-1 bg-transparent border-none cursor-pointer`}>
              <span className={`flex items-center justify-center w-[52px] h-[30px] rounded-full ${
                on ? 'bg-[var(--p-nav-pill)] text-[var(--p-nav-icon)]' : MUTED}`}>
                <Icon size={20} weight={on ? 'bold' : 'regular'} />
              </span>
              <span className={`text-[12px] font-semibold ${on ? 'text-[var(--p-nav-icon)]' : MUTED}`}>{label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default function PremiumToday({
  userId, profile, trend, today: todayProp, onOpenProfile, onNavigate, onOpenConnections,
  pendingContext, contextSaving, onContextSave, onContextSkip, onDeepAnalyze, deepAnalyzing, onViewDeepAnalysis,
}) {
  const today = useMemo(() => todayProp || new Date(), [todayProp]);
  const todayIso = isoOf(today);
  const days = useMemo(() => weekDays(today), [today]);
  const [selected, setSelected] = useState(todayIso);
  const [pick, setPick] = useState(null);              // the activity shown when a day has several
  const [plans, setPlans] = useState(null);           // null = loading
  const [acts, setActs] = useState(null);             // null = loading; [] = none / no feed
  const [hasFeed, setHasFeed] = useState(false);
  const [garmin, setGarmin] = useState(null);
  const [splitsById, setSplitsById] = useState({});

  useEffect(() => {
    if (!userId) return undefined;
    let live = true;
    authFetch(`${API_BASE_URL}/weekly-plan/all-active?user_id=${encodeURIComponent(userId)}`)
      .then((r) => (r && r.ok ? r.json() : null))
      .then((d) => { if (live) setPlans(d || []); })
      .catch(() => { if (live) setPlans([]); });
    fetchGarminActivities(30)
      .then((list) => { if (live) { setActs(list); setHasFeed(list.length > 0); } })
      .catch(() => { if (live) { setActs([]); setHasFeed(false); } });
    fetchGarminStatus()
      .then((s) => { if (live) setGarmin(s); })
      .catch(() => { /* unknown status: no device row (a failed request is never shown as "not connected") */ });
    return () => { live = false; };
  }, [userId]);

  const planByIso = useMemo(() => planDaysByDate(plans), [plans]);
  const runs = useMemo(() => runsOn(acts, selected), [acts, selected]);
  const act = runs.find((r) => r.garmin_id === pick) || runs[0] || trendActivity(trend, selected);
  const garminId = act && act.source === 'garmin' ? act.garmin_id : null;

  useEffect(() => {
    if (!garminId || splitsById[garminId] !== undefined) return undefined;
    let live = true;
    fetchGarminSplits(garminId)
      .then((s) => { if (live) setSplitsById((m) => ({ ...m, [garminId]: s })); })
      .catch(() => { if (live) setSplitsById((m) => ({ ...m, [garminId]: [] })); });
    return () => { live = false; };
  }, [garminId, splitsById]);

  const selectDay = (iso) => { setSelected(iso); setPick(null); };
  const statusOf = (iso) => dayStatus(iso, todayIso, runsOn(acts, iso).length > 0 || !!trendActivity(trend, iso),
    planByIso[iso]);
  const loading = plans === null || acts === null;
  const summary = loading ? null : weekSummary(days, acts, planByIso, todayIso, hasFeed);
  const next = nextWorkoutView(trend && trend.next_workout, planByIso, todayIso);
  const initial = String((profile && profile.name) || '').trim().charAt(0) || null;
  const connect = () => { requestTrainingConnectionsScroll(); if (onOpenConnections) onOpenConnections(); };

  return (
    <div className="premium-v2 flex flex-col gap-[18px]" dir="rtl" data-testid="premium-today">
      <header className="flex items-start justify-between pt-1">
        <div>
          <div className={`text-[13px] font-medium ${MUTED}`}>{headerDate(today)}</div>
          <h1 className={`m-0 text-[26px] font-extrabold leading-tight ${TEXT}`}>היום</h1>
        </div>
        <button type="button" onClick={onOpenProfile} aria-label="פרופיל" data-testid="avatar"
                className={`${FOCUS} w-11 h-11 rounded-full bg-[var(--p-card)] border border-[var(--p-border)] text-[var(--p-accent)] text-[17px] font-extrabold flex items-center justify-center cursor-pointer`}>
          {initial || <span className="w-2 h-2 rounded-full bg-[var(--p-accent)]" />}
        </button>
      </header>

      <DayStrip days={days} selected={selected} todayIso={todayIso} statusOf={statusOf} onSelect={selectDay} />

      {loading ? (                                             // g. data loading -> skeleton loaders
        <div className="flex flex-col gap-[18px]" data-testid="today-loading" role="status" aria-label="טוען נתונים">
          <div className={`${CARD} p-[18px]`}>
            <Skel className="h-5 w-1/3" />
            <div className="mt-4 flex items-center justify-between gap-3">
              <Skel className="w-[124px] h-[124px] rounded-full" />
              <div className="flex-1 flex flex-col items-end gap-2"><Skel className="h-11 w-2/3" /><Skel className="h-4 w-1/3" /></div>
            </div>
            <Skel className="mt-4 h-[62px]" />
          </div>
          <div className={`${CARD} p-4`}><Skel className="h-12" /></div>
          <div className={`${CARD} p-4`}><Skel className="h-20" /></div>
        </div>
      ) : (
        <>
          <DeviceRow garmin={garmin} onConnect={connect} />
          <WorkoutCard iso={selected} todayIso={todayIso} act={act} runs={runs} onPickRun={setPick}
                       planDay={planByIso[selected]} splits={garminId ? splitsById[garminId] : null}
                       noPlan={!hasRunningPlan(plans)} onOpenPlan={() => onNavigate && onNavigate('weekly-plan')} />
          <InsightCard trend={trend} pendingContext={pendingContext} contextSaving={contextSaving}
                       onContextSave={onContextSave} onContextSkip={onContextSkip} onDeepAnalyze={onDeepAnalyze}
                       deepAnalyzing={deepAnalyzing} onViewDeepAnalysis={onViewDeepAnalysis} />
          <NextWorkout view={next} onOpen={() => onNavigate && onNavigate('weekly-plan')} />
          <WeekCard summary={summary} range={weekRangeLabel(days)} />
        </>
      )}
    </div>
  );
}
